/* eslint-disable @typescript-eslint/ban-ts-comment */
import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import AddIcon from "../../components/Icons/AddIcon";
import Button from "../../components/Shared/Button";
import { GetAllHostedQuizs } from "../../api/Quiz/GetAllHostedQuizs";
import Spinner from "../../components/Shared/Spinner";
import TrashIcon from "../../components/Icons/TrashIcon";
import DeleteModal from "../../components/Shared/DeleteModal";
import { DeleteMCQ } from "../../api/Quiz/DeleteQuiz";
import QRCode from "react-qr-code";
import { HostedQuizsDTO } from "../../api/Quiz/GetAllHostedQuizs";
import { Link } from "react-router-dom";
import { Copy } from "lucide-react";
import { axios } from "../../lib/axios";
import Input from "../../components/Shared/Input";
import { ButtonTypes } from "../../utils/consts";
import TextArea from "../../components/Shared/TextArea";
import MultipleUpload from "../../components/Shared/MultipleUpload";
import Upload from "../../components/Shared/Upload";

interface Choice {
  answer: string;
  isCorrect: boolean;
}

interface Question {
  id: number;
  name: string;
  choices: Choice[];
  correctAnswerIndex: number;
  explanation: string;
  figure: string[];
  attachment: string;
}

const HostQuizs = () => {
  const [isMCQOpen, setIsMCQOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [isCreatingQuiz, setIsCreatingQuiz] = useState(false);
  const [currentQuizId, setCurrentQuizId] = useState<string | null>(null);

  // Quiz creation states
  const [errors, setErrors] = useState({
    title: '',
    questions: [] as string[],
  });
  const [submittedQuestions, setSubmittedQuestions] = useState<number[]>([]);
  const [mcqQuestions, setMcqQuestions] = useState<Question[]>([
    {
      id: 1,
      name: "",
      choices: [
        { answer: "", isCorrect: true },
        { answer: "", isCorrect: false },
        { answer: "", isCorrect: false },
        { answer: "", isCorrect: false },
      ],
      correctAnswerIndex: 0,
      explanation: "",
      attachment: "",
      figure: [],
    },
  ]);
  const [nextMcqId, setNextMcqId] = useState(2);
  const [quizTitle, setQuizTitle] = useState("");

  const { data: QuizsResponse, isLoading: isBlogsLoading, refetch } = useQuery({
    queryFn: GetAllHostedQuizs,
    queryKey: ["hosted"],
    staleTime: 0,
    refetchInterval: 0,
  });

  const quizs = QuizsResponse?.data || [];

  const { mutate: generateQuizId, isPending: isGeneratingId } = useMutation({
    mutationFn: async () => {
      return axios.post("/quiz/generate/id");
    },
    onSuccess: (data) => {
      setCurrentQuizId(data.data.id);
      setIsCreatingQuiz(true);
    }
  });

  const { mutate: updateQuiz, isPending: isSubmitting } = useMutation({
    mutationFn: async () => {
      if (quizTitle.trim() !== "" && currentQuizId) {
        return axios.patch(`/quiz/${currentQuizId}`, { title: quizTitle }, {
          params: {
            isHost: true
          }
        });
      }
    },
    onSuccess: () => {
      toast.success("Quiz created successfully");
      setIsCreatingQuiz(false);
      setCurrentQuizId(null);
      setQuizTitle("");
      setMcqQuestions([
        {
          id: 1,
          name: "",
          choices: [
            { answer: "", isCorrect: true },
            { answer: "", isCorrect: false },
            { answer: "", isCorrect: false },
            { answer: "", isCorrect: false },
          ],
          correctAnswerIndex: 0,
          explanation: "",
          attachment: "",
          figure: [],
        },
      ]);
      setSubmittedQuestions([]);
      setNextMcqId(2);
      refetch();
    }
  });

  const { mutate: submitQuestion, isPending } = useMutation({
    mutationFn: async (question: {
      question: string; explanation: string; attachment: string; figure: string[]; choices: { answer: string; isCorrect: boolean }[]
    }) => {
      return axios.post(`/quiz/${currentQuizId}/assign-question`, question);
    },
    onSuccess: () => {
      toast.success("Question submitted successfully");
    }
  });

  const { mutate: deleteQuestion, isPending: isDeleting } = useMutation({
    mutationFn: async (questionId: string) => {
      return axios.delete(`/quiz/${currentQuizId}/questions/${questionId}`);
    },
    onSuccess: () => {
      toast.success("Question deleted successfully");
    }
  });

  const validateQuestion = (question: Question) => {
    let hasError = false;
    const index = mcqQuestions.indexOf(question);
    const questionErrors: string[] = [];
    if (!question.name.trim() || question.choices.some(choice => !choice.answer.trim())) {
      questionErrors[index] = "All questions and choices are required.";
      hasError = true;
    } else {
      questionErrors[index] = "";
    }
    setErrors(prev => ({ ...prev, questions: questionErrors }));
    return !hasError;
  };

  const handleAddQuestion = () => {
    const newQuestion: Question = {
      id: nextMcqId,
      name: "",
      choices: [
        { answer: "", isCorrect: true },
        { answer: "", isCorrect: false },
        { answer: "", isCorrect: false },
        { answer: "", isCorrect: false },
      ],
      correctAnswerIndex: 0,
      explanation: "",
      attachment: "",
      figure: [],
    };
    setMcqQuestions([...mcqQuestions, newQuestion]);
    setNextMcqId(nextMcqId + 1);
  };

  const handleDeleteQuestion = (questionId: number) => {
    if (submittedQuestions.includes(questionId)) {
      deleteQuestion(questionId.toString(), {
        onSuccess: () => {
          setMcqQuestions((prevQuestions) => prevQuestions.filter((question) => question.id !== questionId));
        }
      });
    } else {
      setMcqQuestions((prevQuestions) => prevQuestions.filter((question) => question.id !== questionId));
    }
  };

  const handleQuestionFieldChange = (
    questionIndex: number,
    field: string,
    value: string | boolean,
    choiceIndex?: number,
  ) => {
    setMcqQuestions((prevQuestions) =>
      prevQuestions.map((question, index) => {
        if (index === questionIndex) {
          if (typeof choiceIndex !== "undefined") {
            const updatedChoices = question.choices.map((choice, idx) =>
              idx === choiceIndex ? { ...choice, [field]: value } : choice
            );
            return { ...question, choices: updatedChoices };
          } else {
            return { ...question, [field]: value };
          }
        }
        return question;
      })
    );
  };

  const handleCorrectAnswerChange = (
    questionIndex: number,
    selectedChoiceIndex: number,
  ) => {
    setMcqQuestions((prevQuestions) =>
      prevQuestions.map((question, index) => {
        if (index === questionIndex) {
          const updatedChoices = question.choices.map((choice, idx) => ({
            ...choice,
            isCorrect: idx === selectedChoiceIndex,
          }));
          return {
            ...question,
            choices: updatedChoices,
            correctAnswerIndex: selectedChoiceIndex,
          };
        }
        return question;
      })
    );
  };

  const handleSubmit = (event: { preventDefault: () => void }) => {
    event.preventDefault();
    if (quizTitle.trim() !== "") {
      updateQuiz();
    }
  };

  const handleFileUploaded = (url: string, questionId: number) => {
    setMcqQuestions((prevQuestions) => {
      const updatedQuestions = prevQuestions.map((question) =>
        question.id === questionId
          ? { ...question, figure: [...question.figure, url] }
          : question
      );
      return updatedQuestions;
    });
  };

  const handleAttachmentUpload = (url: string, questionId: number) => {
    setMcqQuestions((prevQuestions) => {
      const updatedQuestions = prevQuestions.map((question) =>
        question.id === questionId
          ? { ...question, attachment: url }
          : question
      );
      return updatedQuestions;
    });
  };

  const downloadQR = (qrCode: string, name: string) => {
    const svgElement = document.getElementById(`qr-code-${qrCode}`);
    if (svgElement) {
      const svgData = new XMLSerializer().serializeToString(svgElement);
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");

      if (ctx) {
        const img = new Image();
        img.onload = () => {
          canvas.width = img.width;
          canvas.height = img.height;
          ctx.drawImage(img, 0, 0);
          const pngFile = canvas.toDataURL("image/png");

          const downloadLink = document.createElement("a");
          downloadLink.download = `QRCode-${name}.png`;
          downloadLink.href = `${pngFile}`;
          downloadLink.click();
        };
        img.src = "data:image/svg+xml;base64," + btoa(svgData);
      } else {
        console.error("Failed to get drawing context for the canvas");
      }
    } else {
      console.error("SVG element not found for QR code");
    }
  };

  const handleDeleteMCQ = async (id: string) => {
    await DeleteMCQ(id);
    toast.success("MCQ Quiz deleted successfully");
  };

  const copyQuizUrl = (qrCode: string) => {
    const url = `${window.location.origin}/hosted-quiz/${qrCode}`;
    navigator.clipboard
      .writeText(url)
      .then(() => {
        toast.success("Quiz URL copied to clipboard!");
      })
      .catch((err) => {
        toast.error("Failed to copy URL");
        console.error("Failed to copy:", err);
      });
  };

  const handleCancel = () => {
    setIsCreatingQuiz(false);
    setCurrentQuizId(null);
    setQuizTitle("");
    setMcqQuestions([
      {
        id: 1,
        name: "",
        choices: [
          { answer: "", isCorrect: true },
          { answer: "", isCorrect: false },
          { answer: "", isCorrect: false },
          { answer: "", isCorrect: false },
        ],
        correctAnswerIndex: 0,
        explanation: "",
        attachment: "",
        figure: [],
      },
    ]);
    setSubmittedQuestions([]);
    setNextMcqId(2);
  };

  if (isBlogsLoading)
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Spinner />
      </div>
    );

  // Show quiz creation form
  if (isCreatingQuiz) {
    return (
      <div className="py-7">
        <h1 className="text-burntBlack font-bold text-[32px] mb-6">
          Host new quiz
        </h1>
        <form
          onSubmit={handleSubmit}
          className="p-8 rounded-3xl shadow bg-white border border-black/15 min-h-[70vh] space-y-5"
        >
          <div className="w-full max-w-[544px] mx-auto">
            <Input
              className="mb-10 text-lg font-semibold"
              inputStyles="bg-[#F5F6F8] !h-12 border-black/15 !rounded"
              title="Quiz Title"
              placeholder=""
              value={quizTitle}
              name="title"
              onChange={(e) => setQuizTitle(e.target.value)}
            />

            <div>
              <h1 className="text-2xl font-bold mb-2.5">Questions</h1>
              <p className="font-medium text-lightGray">
                Nice text goes here to explain the question creation part like
                never before
              </p>
            </div>
            <div>
              {mcqQuestions.map((question, questionIndex) => (
                <div key={question.id}>
                  {errors.questions && errors.questions[questionIndex] && (
                    <div className="mb-2 text-sm text-red-500">
                      {errors.questions[questionIndex]}
                    </div>
                  )}
                  <div className="mt-9">
                    <Input
                      className="text-lg font-semibold"
                      inputStyles="bg-[#F5F6F8] !h-12 border-black/15 !rounded mb-10"
                      title="Question name"
                      placeholder=""
                      name="name"
                      value={question.name}
                      onChange={(e) =>
                        handleQuestionFieldChange(
                          questionIndex,
                          "name",
                          e.target.value,
                          undefined,
                        )
                      }
                      type="text"
                      disabled={submittedQuestions.includes(question.id)}
                    />
                    {errors.questions && errors.questions[questionIndex] && (
                      <div className="mb-2 text-sm text-red-500">
                        {errors.questions[questionIndex]}
                      </div>
                    )}
                    <p className="mb-4 font-medium text-lightGray">
                      Please mark the correct answer among them
                    </p>
                    <div className="grid grid-cols-2 gap-x-6 gap-y-4">
                      {question.choices.map((choice, choiceIndex) => (
                        <div key={`choice-${choiceIndex}`}>
                          <div className="flex items-center justify-between">
                            <label
                              htmlFor={`choice-radio-${questionIndex}-${choiceIndex}`}
                              className="text-lg font-semibold"
                            >
                              {`Choice ${choiceIndex + 1}`}
                            </label>
                            <input
                              id={`choice-radio-${questionIndex}-${choiceIndex}`}
                              type="radio"
                              value={choiceIndex}
                              name={`question-${question.id}-choices`}
                              checked={
                                choiceIndex === question.correctAnswerIndex
                              }
                              onChange={() =>
                                handleCorrectAnswerChange(
                                  questionIndex,
                                  choiceIndex,
                                )
                              }
                              className="w-5 h-5 bg-gray-100 border-2 text-primary border-black/30 focus:ring-0"
                              disabled={submittedQuestions.includes(question.id)}
                            />
                          </div>
                          <Input
                            className="text-lg font-semibold"
                            inputStyles="bg-[#F5F6F8] !h-12 border-black/15 !rounded mb-10"
                            placeholder=""
                            name={`choice-${choiceIndex}`}
                            value={choice.answer}
                            onChange={(e) =>
                              handleQuestionFieldChange(
                                questionIndex,
                                "answer",
                                e.target.value,
                                choiceIndex,
                              )
                            }
                            type="text"
                            disabled={submittedQuestions.includes(question.id)}
                          />
                        </div>
                      ))}
                    </div>
                    <TextArea
                      className="mb-10 text-lg font-semibold"
                      inputStyles="!bg-[#F5F6F8] h-24 border-black/15 !rounded"
                      title="Explanation (Optional)"
                      placeholder=""
                      name="explanation"
                      value={question.explanation}
                      onChange={(e) =>
                        handleQuestionFieldChange(
                          questionIndex,
                          "explanation",
                          e.target.value,
                          undefined
                        )
                      }
                      disabled={submittedQuestions.includes(question.id)}
                    />
                    <MultipleUpload
                      folder="courses_images"
                      onFileUploaded={(url) =>
                        handleFileUploaded(url, question.id)
                      }
                      questionId={question.id}
                    >
                      <Input
                        upload={true}
                        className="mb-10 text-lg font-semibold"
                        inputStyles="bg-[#F5F6F8] !h-12 border-black/15 !rounded"
                        placeholder=""
                        title="Figures"
                        name="img_url"
                        type="text"
                        disabled={submittedQuestions.includes(question.id)}
                      />
                    </MultipleUpload>
                    <Upload
                      acceptedFiles={{ "pdf": [] }}
                      folder="quiz_resources"
                      onFileUploaded={(url) => handleAttachmentUpload(url, question.id)}
                    >
                      <Input
                        upload={true}
                        className="text-lg font-semibold"
                        inputStyles="bg-[#F5F6F8] !h-12 border-black/15 !rounded"
                        placeholder=""
                        title="Attachments"
                        name="attachment"
                        type="text"
                        disabled={submittedQuestions.includes(question.id)}
                      />
                    </Upload>
                    {submittedQuestions.includes(question.id) ? null : (
                      <button
                        type="button"
                        disabled={isPending}
                        className="flex justify-center items-center h-16 font-medium text-xl rounded-[10px] transition-all duration-300 ease-in-out bg-secondary text-white hover:bg-[#a32c07] !h-12 w-40 !text-base !font-bold mt-4"
                        onClick={async () => {
                          const submittedQuestion = {
                            question: question.name,
                            explanation: question.explanation,
                            attachment: question.attachment,
                            figure: question.figure,
                            choices: question.choices
                          };
                          if (validateQuestion(question)) {
                            submitQuestion(submittedQuestion, {
                              onSuccess: (data) => {
                                const newId = data?.data?.id;
                                if (newId) {
                                  setMcqQuestions((prevQuestions) =>
                                    prevQuestions.map((q) =>
                                      q.id === question.id ? { ...q, id: newId } : q
                                    )
                                  );
                                  setSubmittedQuestions((prev) => [...prev, newId]);
                                } else {
                                  setSubmittedQuestions((prev) => [...prev, question.id]);
                                }
                              }
                            });
                          }
                        }}
                      >
                        Submit Question
                      </button>
                    )}
                  </div>
                  <div className="flex justify-end gap-5 mt-10">
                    <Button
                      onClick={() => handleDeleteQuestion(question.id)}
                      className={`before:!bg-primary hover:text-white !text-base !font-bold !h-12 w-40 !border-primary !border-2 text-primary ${mcqQuestions.length === 1
                        ? "opacity-50 cursor-not-allowed"
                        : ""
                        }`}
                      disabled={mcqQuestions.length === 1 || isDeleting}
                      type={ButtonTypes.BUTTON}
                      variant="transparent"
                    >
                      Delete
                    </Button>
                    <Button
                      onClick={() => handleAddQuestion()}
                      type={ButtonTypes.BUTTON}
                      variant="primary"
                      className="!h-12 w-40 !text-base !font-bold "
                    >
                      Add New Question
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="flex justify-between w-full">
            <Button
              onClick={handleCancel}
              className={`mt-8 !bg-white border-black/20 border !text-burntBlack w-[160px] !h-11 !text-base font-bold`}
              variant="primary"
              type={ButtonTypes.BUTTON}
            >
              Cancel
            </Button>
            <Button
              type={ButtonTypes.SUBMIT}
              disabled={isSubmitting}
              className={`mt-8 w-[160px] !h-11 !text-base font-bold`}
              variant="primary"
            >
              Send Replies
            </Button>
          </div>
        </form>
      </div>
    );
  }

  // Show quiz list
  return (
    <div>
      <div className="pb-10">
        <div className="flex items-center justify-between py-7">
          <h1 className="text-burntBlack font-bold text-[32px]">
            All Hosted Quizs
          </h1>
          <Button
            onClick={() => generateQuizId()}
            className="!h-12 w-48 !text-sm font-bold rounded-md !bg-[#4379EE] hover:!bg-primary"
            variant="primary"
            disabled={isGeneratingId}
          >
            <AddIcon className="mr-1.5" />
            Host a new Quiz
          </Button>
        </div>
        <div className="grid grid-cols-3 gap-6">
          {quizs &&
            quizs.map((mcq: HostedQuizsDTO, index: number) => (
              <div
                key={mcq.id}
                className="relative flex flex-col items-center justify-between py-4 border rounded-lg border-black/15"
              >
                <h1 className="text-center">
                  MCQ {index + 1}. {mcq.title}
                </h1>
                <span className="mt-2 mb-4 font-bold">
                  QR Code: {mcq.qrCode}
                </span>
                <QRCode
                  id={`qr-code-${mcq.qrCode}`}
                  size={200}
                  value={
                    `${window.location.origin}/hosted-quiz/${mcq.qrCode}` ||
                    "00000"
                  }
                  level="H"
                />
                <button
                  onClick={() => copyQuizUrl(mcq.qrCode)}
                  className="p-2 mx-auto mt-3 bg-gray-100 border rounded-full border-secondary w-fit"
                  title="Copy quiz URL"
                >
                  <Copy className="text-secondary" />
                </button>
                <Link to={`/dashboard/host-quizs/scores/${mcq.id}`}>
                  <Button
                    className=" w-48 !text-sm font-bold !h-11 mt-4 !bg-[#4379EE] hover:!bg-primary"
                    variant="primary"
                  >
                    Student Scores
                  </Button>
                </Link>
                <Button
                  className="before:!bg-primary hover:text-white !text-base !font-bold !h-11 my-4 w-48 !border-primary !border-2 text-primary"
                  variant="transparent"
                  onClick={() => downloadQR(mcq.qrCode, mcq.title)}
                >
                  Download QR Code
                </Button>

                <button
                  onClick={() => {
                    setSelectedId(mcq.id);
                    setIsMCQOpen(true);
                  }}
                  className="flex items-center gap-2 px-4 py-2 text-white transition-all duration-300 bg-red-500 rounded hover:bg-red-900"
                >
                  Delete <TrashIcon />
                </button>
                <DeleteModal
                  isOpen={isMCQOpen}
                  setIsOpen={setIsMCQOpen}
                  mutationFn={() => handleDeleteMCQ(selectedId!)}
                  itemId={selectedId!}
                  queryKey="hosted"
                />
              </div>
            ))}
        </div>
      </div>
    </div>
  );
};

export default HostQuizs;